import { FormattedPaymentMethodTypes } from "types/paymentMethodComponent";
import {
  PaymentGateway,
  PaymentMethodList,
  SettingsValue,
} from "types/payments";
import { isCardExpiryUsable } from "utils/cardExpiration";

type PaymentPermission = Record<string, any> | null | undefined;

export interface UsableSavedPaymentMethods {
  cards: FormattedPaymentMethodTypes[];
  banks: FormattedPaymentMethodTypes[];
  wallets: FormattedPaymentMethodTypes[];
  hasUsableSavedMethods: boolean;
}

const walletMethodNames: Record<string, string> = {
  PP: "PayPal",
  CAPP: "Cash App Pay",
  GP: "Google Pay",
  AP: "Apple Pay",
  AMZP: "Amazon Pay",
};

const walletPermissionKeys: Record<string, string> = {
  PP: "pp",
  CAPP: "capp",
  GP: "gp",
  AP: "ap",
  AMZP: "amzp",
};

const walletFlowMethodNames: Record<string, string> = {
  ...walletMethodNames,
  GP: "Card",
  AP: "Card",
};

const isAllowedForFlow = (
  methodName: string,
  paymentMethodList?: Pick<PaymentMethodList, "name">[]
) =>
  paymentMethodList === undefined ||
  paymentMethodList.some((method) => method?.name === methodName);

const isAvailable = (method: FormattedPaymentMethodTypes) =>
  method?.available === true;

const isUsableCard = (method: FormattedPaymentMethodTypes) => {
  if (!isAvailable(method) || method?.isExpired) return false;
  return isCardExpiryUsable(method?.cc_expiry);
};

const isUsableBank = (method: FormattedPaymentMethodTypes) =>
  isAvailable(method) && method?.is_connected === "Y";

export const isSavedWalletAllowed = (
  method: FormattedPaymentMethodTypes,
  paymentPermission: PaymentPermission,
  paymentMethodList?: Pick<PaymentMethodList, "name">[]
) => {
  if (!isAvailable(method)) return false;

  const permissionKey = walletPermissionKeys[method?.payment_method];
  const methodName = walletMethodNames[method?.payment_method];
  const flowMethodName = walletFlowMethodNames[method?.payment_method];
  if (
    !permissionKey ||
    !methodName ||
    !flowMethodName ||
    !isAllowedForFlow(flowMethodName, paymentMethodList)
  ) {
    return false;
  }

  if (
    permissionKey === "pp" &&
    paymentPermission?.pp_gateway_name?.name === PaymentGateway.STAX
  ) {
    return false;
  }

  const availability =
    paymentPermission?.[`${permissionKey}_ext_availability`] ??
    paymentPermission?.[`${permissionKey}_int_availability`];

  return availability === SettingsValue.YES;
};

export const getUsableSavedPaymentMethods = ({
  cardList = [],
  bankList = [],
  walletList = [],
  paymentMethodList,
  paymentPermission,
}: {
  cardList?: FormattedPaymentMethodTypes[];
  bankList?: FormattedPaymentMethodTypes[];
  walletList?: FormattedPaymentMethodTypes[];
  paymentMethodList?: Pick<PaymentMethodList, "name">[];
  paymentPermission?: PaymentPermission;
}): UsableSavedPaymentMethods => {
  const cards = isAllowedForFlow("Card", paymentMethodList)
    ? cardList.filter(isUsableCard)
    : [];
  const banks = isAllowedForFlow("Bank", paymentMethodList)
    ? bankList.filter(isUsableBank)
    : [];
  const wallets = walletList.filter((method) =>
    isSavedWalletAllowed(method, paymentPermission, paymentMethodList)
  );

  return {
    cards,
    banks,
    wallets,
    hasUsableSavedMethods:
      cards.length > 0 || banks.length > 0 || wallets.length > 0,
  };
};
